import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building,
  Smartphone,
  Plus,
  Edit,
  Trash2,
  CheckCircle2,
  AlertCircle,
  X,
  ArrowLeft,
  Shield,
  Layers,
} from 'lucide-react';
import Navbar from '../../components/feature/Navbar';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface PaymentInstruction {
  id: string;
  channel_type: 'Bank Transfer' | 'Mobile Money' | 'Cash Handover' | 'Other';
  provider: string;
  account_number: string;
  account_holder: string;
  branch: string;
  currency: string;
  instructions: string;
  is_active: boolean;
  display_order: number;
}

export default function PaymentInstructionsPage() {
  const { user, isStaff, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [instructions, setInstructions] = useState<PaymentInstruction[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [channelType, setChannelType] = useState<'Bank Transfer' | 'Mobile Money' | 'Cash Handover' | 'Other'>('Mobile Money');
  const [provider, setProvider] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('BIENTOMART DIGITAL SOLUTIONS');
  const [branch, setBranch] = useState('Kampala');
  const [currency, setCurrency] = useState('UGX');
  const [instructionText, setInstructionText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchInstructions();
  }, [user]);

  const fetchInstructions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('payment_instructions')
        .select('*')
        .order('display_order', { ascending: true });

      if (!error && data) {
        setInstructions(data as PaymentInstruction[]);
      }
    } catch (err) {
      console.error('Error fetching instructions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setChannelType('Mobile Money');
    setProvider('');
    setAccountNumber('');
    setAccountHolder('BIENTOMART DIGITAL SOLUTIONS');
    setBranch('Kampala');
    setCurrency('UGX');
    setInstructionText('');
    setIsActive(true);
    setShowModal(true);
  };

  const handleOpenEdit = (inst: PaymentInstruction) => {
    setEditingId(inst.id);
    setChannelType(inst.channel_type);
    setProvider(inst.provider);
    setAccountNumber(inst.account_number);
    setAccountHolder(inst.account_holder);
    setBranch(inst.branch || '');
    setCurrency(inst.currency);
    setInstructionText(inst.instructions);
    setIsActive(inst.is_active);
    setShowModal(true);
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await supabase
        .from('payment_instructions')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      setInstructions((prev) =>
        prev.map((i) => (i.id === id ? { ...i, is_active: !currentStatus } : i))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveInstruction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!provider || !accountNumber || !accountHolder) return;

    setSaving(true);
    try {
      if (editingId) {
        await supabase
          .from('payment_instructions')
          .update({
            channel_type: channelType,
            provider,
            account_number: accountNumber,
            account_holder: accountHolder,
            branch,
            currency,
            instructions: instructionText,
            is_active: isActive,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingId);
      } else {
        await supabase.from('payment_instructions').insert({
          channel_type: channelType,
          provider,
          account_number: accountNumber,
          account_holder: accountHolder,
          branch,
          currency,
          instructions: instructionText,
          is_active: isActive,
          display_order: instructions.length + 1,
        });
      }

      setShowModal(false);
      await fetchInstructions();
    } catch (err) {
      console.error('Error saving payment instruction:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <Navbar />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link
                to="/admin"
                className="text-xs font-semibold text-gray-500 hover:text-darkBlue-900 flex items-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to Admin Console</span>
              </Link>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-darkBlue-900">
              Payment Instructions & Bank Channels
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Configure active Mobile Money and Bank wire channels displayed to clients for manual payments.
            </p>
          </div>

          <button
            onClick={handleOpenAdd}
            className="px-4 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md transition-all whitespace-nowrap self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Add Payment Method</span>
          </button>
        </div>

        {/* Instructions Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-clairBlue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-xs text-gray-400 font-semibold">Loading payment channels...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {instructions.map((inst) => (
              <div
                key={inst.id}
                className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-clairBlue-50 text-clairBlue-700 border border-clairBlue-200">
                      {inst.channel_type}
                    </span>
                    <button
                      onClick={() => handleToggleActive(inst.id, inst.is_active)}
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold cursor-pointer transition-colors ${
                        inst.is_active
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {inst.is_active ? 'Active' : 'Disabled'}
                    </button>
                  </div>

                  <h3 className="font-bold text-base text-darkBlue-900 mb-1">{inst.provider}</h3>

                  <div className="bg-gray-50 p-3 rounded-2xl border border-gray-100 space-y-1 text-xs mb-3 font-mono">
                    <div>
                      <span className="text-gray-400">Account #:</span>{' '}
                      <strong className="text-darkBlue-900">{inst.account_number}</strong>
                    </div>
                    <div>
                      <span className="text-gray-400">Holder:</span>{' '}
                      <strong className="text-darkBlue-900">{inst.account_holder}</strong>
                    </div>
                    {inst.branch && (
                      <div>
                        <span className="text-gray-400">Branch:</span>{' '}
                        <span className="text-gray-700">{inst.branch}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-400">Currency:</span>{' '}
                      <span className="text-gray-700">{inst.currency}</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">
                    {inst.instructions}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-2 mt-4">
                  <button
                    onClick={() => handleOpenEdit(inst)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    <span>Edit Channel</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal for Add/Edit Payment Channel */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 border border-gray-100 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
                <h3 className="text-lg font-bold text-darkBlue-900">
                  {editingId ? 'Edit Payment Channel' : 'Add Official Payment Channel'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleSaveInstruction} className="space-y-4 text-xs">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Channel Type *</label>
                  <select
                    value={channelType}
                    onChange={(e) => setChannelType(e.target.value as any)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  >
                    <option value="Mobile Money">Mobile Money (MTN / Airtel)</option>
                    <option value="Bank Transfer">Bank Wire / Transfer</option>
                    <option value="Cash Handover">Cash Handover / Physical Office</option>
                    <option value="Other">Other Custom Channel</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">Provider / Bank Name *</label>
                  <input
                    type="text"
                    required
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    placeholder="e.g. MTN Mobile Money (Uganda) or Stanbic Bank"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">Account Number / Phone *</label>
                    <input
                      type="text"
                      required
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="e.g. +256 773 515 447"
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">Account Holder Name *</label>
                    <input
                      type="text"
                      required
                      value={accountHolder}
                      onChange={(e) => setAccountHolder(e.target.value)}
                      placeholder="e.g. BIENTOMART DIGITAL SOLUTIONS"
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">Branch (Optional)</label>
                    <input
                      type="text"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      placeholder="e.g. Kampala Road Branch"
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">Currency</label>
                    <input
                      type="text"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      placeholder="UGX or USD"
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl uppercase font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">Customer Payment Instructions *</label>
                  <textarea
                    rows={3}
                    required
                    value={instructionText}
                    onChange={(e) => setInstructionText(e.target.value)}
                    placeholder="Step-by-step instructions for the client (e.g. Dial *165#, select Send Money, enter number...)"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl resize-none"
                  />
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="rounded border-gray-300 text-darkBlue-900 focus:ring-clairBlue-500"
                    />
                    <span>Active and visible to customers on receipt submission</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 font-semibold text-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl font-bold"
                  >
                    {saving ? 'Saving...' : 'Save Channel'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
